import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { ExperienceProtocolPicker } from '@/components/admin/experiences/timeline/protocol-picker';
import { ProtocolVignette } from '@/components/admin/experiences/timeline/protocol-vignette';
import { PaintedTextField } from '@/components/admin/variables/painted-text-field';
import { VariableMenu } from '@/components/admin/variables/variable-menu';
import { ReferenceControl } from '@/components/admin/versioned/reference-control';
import { Button } from '@/components/ui/button';
import type { PaletteContent } from '@/lib/variable-palette';
import { firstPaletteToken, paletteFullSentence } from '@/lib/variable-palette';
import type { VariableZone } from '@/lib/variable-pill';
import type { ExperiencePageDraft, ProtocolCard, VersionPin } from '@matter_extractor/shared';
import { EXPERIENCE_CONTEXT_MAX } from '@matter_extractor/shared';
import { useId, useState } from 'react';

/**
 * La page protocole dans l'éditeur de page (refonte modulaire §9.5 ; issue
 * #58 ; le Contexte depuis le grill Variables du 2026-09-16, D2, issue #88).
 *
 * §9.5, pour l'éditeur : « le choix du protocole (latest par défaut, une version
 * précise si épinglée - vitrine.md §3.1), le visuel de la page qui prend celui
 * du thème sauf surcharge, le protocole choisi rendu EN LECTURE SEULE avec le
 * dessin de sa card view (§7.1), et le bouton pour le remplacer ». The page's visual is the
 * bar above this panel, which is the same bar every page gets; what is left is
 * this one.
 *
 * ## Rien n'est redessiné, et c'est la phrase de §9.5
 *
 * « Rien n'est redessiné de ce côté » : the card is `ProtocolVignette` and the
 * selector is `ExperienceProtocolPicker`, both built by issue #57 for the
 * scene. A protocol has to look the same wherever it is read, and a second
 * drawing here would be a second protocol - the drift the two sub-trees'
 * `contracts.ts` exist to prevent.
 *
 * ## Ni pile, ni grille « Ajouter », ni aperçu
 *
 * A protocol page « ne porte pas de blocs » (§9.1, §9.5), so the three zones of
 * §9.3 have nothing to hold: an empty stack under an « Ajouter un texte » grid
 * would offer a gesture that produces a block the reader never plays, and the
 * interactive preview would rehearse a conversation « aucune conversation n'y
 * est simulée » forbids. The body therefore lays this page out in two columns -
 * the « Page » panel, then this - rather than three.
 *
 * ## La version jouée est un menu sous la carte
 *
 * `latest` par défaut, une version précise si on l'épingle (vitrine.md V16) :
 * la pastille-menu « Version jouée » de la planche `references.html` se pose
 * sous la carte du protocole, avec le signal « vN disponible » d'une référence
 * épinglée en retard. Un protocole neuf se choisit toujours à `latest` ; le
 * sélecteur n'en demande plus.
 *
 * ## Le Contexte, sous le sélecteur (D2)
 *
 * « Sous le sélecteur de protocole, l'inspecteur de la page protocole porte une
 * zone de texte, Contexte servi à l'interviewer » (§9.5): served to the
 * interviewer on every turn of the protocol, after the persona's overlay and
 * before the global rules (§10.3), bounded like the overlay (H23). It is
 * `content.context` of the EXPERIENCE (H58) - the protocol page is unique, so
 * the two names designate one thing - and this panel is only where it is
 * edited, which is why it writes the draft's `context` and not the page. It
 * accepts the four kinds of variable (H59) within the reach of the protocol
 * page: the fields and choices of the pages before, the objectives, the
 * criteria - the palette the painted field opens follows that place (D3).
 */

export interface ProtocolPagePanelProps {
  readonly page: ExperiencePageDraft;
  /** The library, retired ones included - the selector drops them (§4). */
  readonly protocols: readonly ProtocolCard[];
  /** Whether that read has landed: nothing is claimed before it (§4). */
  readonly protocolsRead: boolean;
  readonly readOnly: boolean;
  /** The protocol the page plays - always at `latest` - or null for « sans protocole » (§9.1). */
  readonly onChoose: (protocolId: string | null) => void;
  /** The pin of the protocol already chosen, from the pill-menu or the signal. */
  readonly onPin: (pin: VersionPin) => void;
  /** The Contexte of §9.5, as stored - tokens included - and its writer. */
  readonly context: string;
  readonly onContext: (next: string) => void;
  /** What each token of the Contexte is, and the `@` menu at its place (D3, D5). */
  readonly zone: VariableZone;
  readonly palette: (query: string) => PaletteContent;
}

export function ProtocolPagePanel({
  page,
  protocols,
  protocolsRead,
  readOnly,
  onChoose,
  onPin,
  context,
  onContext,
  zone,
  palette,
}: ProtocolPagePanelProps) {
  const [picking, setPicking] = useState(false);
  const contextLabelId = useId();

  const card =
    page.protocol === null ? null : (protocols.find((one) => one.id === page.protocol?.id) ?? null);

  return (
    <section
      data-testid={PAGE_EDITOR_IDS.protocolPanel}
      className="min-w-0 space-y-3 rounded-md border-2 border-gray-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-semibold text-base text-bleu-nuit leading-6">
            Le protocole de cette page
          </h2>
          <p className="text-gray-600 text-sm leading-5">
            La conversation se joue ici. Aucune n’est simulée dans l’éditeur : pour l’essayer, il
            faut un lien en développement.
          </p>
        </div>
        {readOnly ? null : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid={EXPERIENCE_IDS.protocolReplace}
            onClick={() => setPicking((was) => !was)}
          >
            {picking ? 'Fermer' : 'Remplacer'}
          </Button>
        )}
      </div>

      {/*
        A CAPPED box and not a sized one. `ProtocolVignette` is `size-full`
        because the scene hands it the frame a page would have occupied; here
        there is no page to match, so the height is left indefinite - `h-full`
        against an indefinite height resolves to auto - and the card hugs its
        own content. The cap is what makes the centre variant's inner
        `overflow-y-auto` bite when a chain of twenty steps arrives.
      */}
      <div className="max-h-80 max-w-2xl">
        <ProtocolVignette
          reference={page.protocol}
          card={card}
          resolved={protocolsRead}
          variant="centre"
        />
      </div>

      {page.protocol === null || card === null ? null : (
        <div className="flex max-w-2xl flex-wrap items-start gap-x-3 gap-y-1">
          <span className="pt-0.5 font-semibold text-gray-700 text-xs">Version jouée</span>
          <ReferenceControl
            subject={{
              kind: 'protocol',
              id: card.id,
              name: card.name,
              who: 'cette expérience',
              pin: page.protocol.pin,
              developmentNumber: card.development.number,
            }}
            readOnly={readOnly}
            testId={EXPERIENCE_IDS.protocolPin}
            onPin={onPin}
          />
        </div>
      )}

      {picking ? (
        <ExperienceProtocolPicker
          protocols={protocols}
          loaded={protocolsRead}
          chosenId={page.protocol?.id ?? null}
          onChoose={(protocolId) => {
            onChoose(protocolId);
            setPicking(false);
          }}
          onClear={() => {
            onChoose(null);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}

      <div className="max-w-2xl space-y-1.5 border-gray-200 border-t pt-3">
        <h3 className="font-semibold text-base text-bleu-nuit leading-6">Contexte</h3>
        <p className="text-gray-600 text-xs leading-4">
          Servi à l’interviewer à chaque tour du protocole, à la suite de la surcouche du persona.
          Ce que l’interviewer doit savoir de cette expérience et de cette personne.
        </p>
        <p id={contextLabelId} className="pt-1 font-semibold text-gray-800 text-sm">
          Contexte servi à l’interviewer
        </p>
        <PaintedTextField
          value={context}
          readOnly={readOnly}
          placeholder="La personne s’appelle @prénom et a choisi le parcours @parcours…"
          ariaLabelledBy={contextLabelId}
          testId={PAGE_EDITOR_IDS.context}
          insertTestId={PAGE_EDITOR_IDS.contextInsert}
          minHeightClass="min-h-32"
          zone={zone}
          renderMenu={(query, insert, fits) => (
            <VariableMenu
              content={palette(query ?? '')}
              query={query}
              onInsert={insert}
              fits={fits}
              full={paletteFullSentence(EXPERIENCE_CONTEXT_MAX)}
              className="mt-2"
            />
          )}
          firstToken={(query) => firstPaletteToken(palette(query))}
          // The counter is the bound, refused and never cut (#160).
          counter={{ max: EXPERIENCE_CONTEXT_MAX, testId: PAGE_EDITOR_IDS.contextCount }}
          onChange={onContext}
        />
      </div>
    </section>
  );
}
